import useAxios from "../../../../utils/useAxios";
import AuthContext from "../../../../context/AuthContext";
import {
  artworkDesignGlobalPermissions,
  artworkMasterDataGlobalPermissions,
} from "../../../master-artwork/helpers/artworkPermissions";
import {
  DebounceContext,
  DebounceContextType,
} from "../../../../context/DebounceContext";
import { useMoldContext } from "../../../../context/MoldContext";
import { getMolds } from "../../../../helpers/mold.helper";
import { fetchMold } from "../../../../hooks/useMold";
import { useDecorationTechContext } from "../../../../context/DecorationTechContext";
import { useColorContext } from "../../../../context/ColorContext";
import { Predicates } from "../../../../libraries/predicates/predicates";
import { fetchColor } from "../../../../hooks/useColor";
import {
  formatColorIntoOption,
  getColors,
} from "../../../../helpers/color.helper";
import Spinner from "../../../../components/Spinner";
import OptionFormatter from "../../../../components/OptionFormatter";
import { ExpandableSection } from "../../../skus/components/expandable-section";
import {
  formatDecorTechIntoOption,
  getDecorTechs,
} from "../../../../helpers/decor-tech.helper";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import AsyncSelect from "react-select/async";
import Select from "react-select";
import ReactModal from "react-modal";
import { useGetCollectionsHook } from "../../hooks/useGetCollectionsHook";
import {
  formatCollectionOptions,
  getCollections,
} from "../../helpers/collections.helper";
import { useCreatePrintingTechniqueStore } from "../../context/printingTechnique/create/CreatePrintingTechniqueContext";
import { CreatePrintingTechniqueTypes } from "../../state/printingTechnique/create/data";
import { PrintingTechniqueActions } from "../../state/printingTechnique/create";
import ArtworkUploadModal from "../../components/artwork-upload-modal";
import ArtworkPictureModal from "../..//components/ArtworkPicutureModal";
import { fetchPrintingTechnique } from "../../helpers/useArtworkFetchPrintingTechnique";
import { ArtworkRequestHistoryTable } from "../../components/ArtworkRequestHistoryComponent";
import { ArtworkPrintingTechniqueActionButtonsGroup } from "../../components/printing-technique-action-buttons/printingTechniqueActionButtons";
import Banner from "../../../../components/Banner";
import { CreateArtworkCollectionState } from "../../state/collection/create/data";

export const ArtworkPrintingTechniqueCretePage = (props: any) => {
  const axios = useAxios();
  const authContext = useContext(AuthContext);
  const username = authContext?.username;
  const { id, code } = useParams();
  const location = useLocation();

  const hasCreatorPermission = artworkDesignGlobalPermissions.every(
    (permission) => authContext?.hasPermission(permission),
  );
  const hasApproverPermission = artworkMasterDataGlobalPermissions.every(
    (permission) => authContext?.hasPermission(permission),
  );

  const [urlSearchParams, setUrlSearchParams] = useSearchParams();
  const [isEditing, setIsEditing] = useState<boolean>(
    () => !!urlSearchParams.get("editing") && hasApproverPermission,
  );

  const { searchDebounce } = useContext<DebounceContextType>(DebounceContext);

  const { state, dispatch } = useCreatePrintingTechniqueStore();

  const { data: moldsFetched, isLoading: isMoldsLoading } = useMoldContext();

  const loadMolds = async (input: string, callback: any) => {
    let options: any = getMolds(moldsFetched);
    if (input.length > 2) {
      const response = await fetchMold({ search: input, axios });
      options = getMolds(response);
    }
    callback(options);
  };


  const [searchCollection, setSearchCollection] = useState("")

  const collectionSearch = useMemo(() => {
    return { status: "APPROVED", search:searchCollection };
  }, [searchCollection]);

  const {
    data: collections,
    isLoading: isCollectionsLoading,getCollection
  } = useGetCollectionsHook({ search: collectionSearch });

  const collectionsOptions = getCollections(collections.results);

  const fetchCollections = useCallback(async (value: any, callback:any) => {

      const response = await getCollection({
        search: { status: "APPROVED", search: value },
      });

      callback(getCollections(response.results));
  }, [collectionsOptions]);

  const { data: decorationTechs, isLoading: isDecoTechsLoading } =
    useDecorationTechContext();

  const { data: colors, isLoading: isColorsLoading } = useColorContext();

  const loadColorOptions = async (search: string, callback: any) => {
    if (Predicates.isNullOrUndefined(search) || search.length < 3) return [];
    const response = await fetchColor({
      search,
      axios,
    });

    callback(getColors(response));
  };

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [isSaveOrSubmitInProgress, setIsSaveOrSubmitInProgress] =
    useState<boolean>(false);

  const [showUploadModal, setShowUploadModal] = useState<boolean>(false);
  const [showPictureModal, setShowPictureModal] = useState<boolean>(false);

  const getMissingConditionsMessage = () => {
    const levelRequired = "full";

    const conditions = [
      {
        condition: (state: any) =>
          state?.name.length > 3 &&
          Predicates.isNotNullAndNotUndefinedAndNotEmpty(state?.name),
        message: `<strong>Must have a name</strong> field is required`,
        conditionLevel: "full",
      },
      {
        condition: (state: any) =>
          Predicates.isNotNullAndNotUndefinedAndNotEmpty(
            state?.collection?.name,
          ),
        message: `<strong>Collection</strong> field is required`,
        conditionLevel: "full",
      },
      {
        condition: (state: any) =>
          Predicates.isNotNullAndNotUndefinedAndNotEmpty(state?.molds),
        message: `<strong>Mold</strong> field is required`,
        conditionLevel: "full",
      },
      {
        condition: (state: any) =>
          Predicates.isNotNullAndNotUndefinedAndNotEmpty(
            state?.decoration_technique?.name,
          ),
        message: `<strong>Decoration technique</strong> field is required`,
        conditionLevel: "full",
      },
      {
        condition: (state: any) =>
          Predicates.isNotNullAndNotUndefinedAndNotEmpty(state?.color?.name),
        message: `<strong>Color</strong> field is required`,
        conditionLevel: "full",
      },
    ];

    const missingConditions = conditions.filter(
      (condition) =>
        (levelRequired === "full"
          ? true
          : condition.conditionLevel === levelRequired) &&
        !condition.condition(state),
    );

    if (missingConditions.length < 1) {
      return "";
    }

    let message =
      "You cannot proceed to collection creation. The following requirements are missing <br> <ul class='mb-0'>";
    message += missingConditions
      .map((condition) => `<li>${condition.message}</li>`)
      .join("");
    return message + "</ul>";
  };

  const [searchParams, setSearchParams] = useState<any>({
    limit: 1,
    lastDays: -1,
    requestor: "",
    context: [45],
    status: ["APPROVED"],
    search: "",
    corresponding_object_id: `${code}`,
    hasFilterApplyed: false,
    offset: 0,
  });

  const [imageToModal, setImageToModal] = useState<any>({
    imageName: `Technical File ${state.name}`,
    image_file: state.image,
  });

  const printingTechnique = async (id: string | number) => {
    const response = await fetchPrintingTechnique({ axios, id: id });

    if (response) {
      const isReadOnly =
        //@ts-ignore
        response.status.name === "APPROVED" ||
        //@ts-ignore
        response.status.name === "REJECTED"
          ? true
          : //@ts-ignore
            !hasApproverPermission && response.status.name === "SUBMITTED";

      dispatch(
        PrintingTechniqueActions.setStateValue(
          CreatePrintingTechniqueTypes.SET_PRINTING_TECHNIQUE,
          {
            ...response,
            isReadOnly: isReadOnly,
            //@ts-ignore
            // image: `data:image/jpeg;base64,${response?.image?.image_base64}`,
            //@ts-ignore
            picture: response?.picture?.image_base64,
          },
        ),
      );
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (!!id && location?.state?.code) {
      //@ts-ignore
      const isReadOnly =
        location.state.status.name === "APPROVED" ||
        location.state.status.name === "REJECTED"
          ? true
          : !hasApproverPermission &&
            location.state.status.name === "SUBMITTED";

      dispatch(
        PrintingTechniqueActions.setStateValue(
          CreatePrintingTechniqueTypes.SET_PRINTING_TECHNIQUE,
          {
            ...location?.state,
            isReadOnly: isReadOnly,
            //@ts-ignore
            // image: `data:image/jpeg;base64,${location?.state?.image?.image_base64}`,
            //@ts-ignore
            picture: location?.state?.picture?.image_base64,
          },
        ),
      );
      return
    } else if (!!id) {
      setIsLoading(true);
      printingTechnique(id);
    }


    dispatch(
      PrintingTechniqueActions.setStateValue(
        CreatePrintingTechniqueTypes.SET_PRINTING_TECHNIQUE,
        {
          id: "",
          code: "",
          collection: {} as CreateArtworkCollectionState,
          image: "",
          name: "",
          isReadOnly: false,
          color: null,
          technical_file_comment: "",
          decoration_technique: null,
          molds: null,
          purchased_parts: "N",
          submit_comment: "",
          history: [],
          status: null,
          isValid: false,
        },
      ),
    );
    setIsEditing(false);
  }, [location, id]);

  useEffect(() => {
    const isValid = !getMissingConditionsMessage();

    dispatch(
      PrintingTechniqueActions.setStateValue(
        CreatePrintingTechniqueTypes.SET_IS_VALID,
        isValid,
      ),
    );
  }, [
    state.collection,
    state.molds,
    state.name,
    state.decoration_technique,
    state.color,
  ]);

  const handlePictureModal = () => {
    if (!state?.picture) {
      setShowUploadModal(!state.image && !state?.isReadOnly);
      setShowPictureModal(!!state.image);

      setImageToModal({
        imageName: `Technical File ${state.name ?? ""}`,
        image_file: state.image,
      });
      return;
    }

    // if (!state.image && !state?.isReadOnly) {
    //   setShowUploadModal(true);
    //   return;
    // }

    setImageToModal({
      imageName: `Technical File ${state.name ?? ""}`,
      image_base64: `data:image/jpeg;base64,${state.picture}`,
      isBlocked: !(!state.image && !state?.isReadOnly),
    });
    setShowPictureModal(true);
  };

  return (
    <>
      {isLoading && <Spinner />}
      {!isLoading && (
        <div
          className="container-fluid mt-3 bg-white shadow py-3 rounded-1 d-flex flex-column justify-contnet-between h-100"
          style={{ minHeight: "70vh" }}
        >
          <h6 className="text-primary fw-bold card-header border-0 mb-2">
            Artwork Hierarchy
          </h6>
          <div className="row mb-0 mt-1">
            <div className="col-md-4 mb-2">
              <label className="form-label">
                Collection{" "}
                <span className="red-text fw-bold">
                  {" "}
                  <span className="red-text fw-bold">*</span>
                </span>
              </label>
              <AsyncSelect
                key={"collectionsOptions"}
                name="collection"
                value={
                  Predicates.isNotNullAndNotUndefined(state?.collection?.name)
                    ? formatCollectionOptions(state?.collection)
                    : null
                }
                onChange={(e: any) => {
                  dispatch(
                    PrintingTechniqueActions.setStateValue(
                      CreatePrintingTechniqueTypes.SET_COLLECTION,
                      e ? e.value : null,
                    ),
                  );
                }}
                defaultOptions={collectionsOptions}
                loadOptions={(input, callback) => {
                  searchDebounce(
                    fetchCollections,
                    input,
                    getCollections(collections?.results),
                    callback,
                  );
                }}
                formatOptionLabel={OptionFormatter}
                isLoading={isCollectionsLoading}
                isClearable
                isSearchable
                placeholder="(min 3 characters)"
                classNamePrefix="react-select"
                isDisabled={state?.isReadOnly}
              />
            </div>

            <div className="col-md-7 mb-0 d-md-flex flex-wrap flex-fill">
              <div className="col-lg-4 mb-0 form-group">
                <label className="form-label">Collection Launch Date</label>
                <textarea
                  rows={1}
                  className="form-control d-inline no-resize horizontal-scroll"
                  value={state?.collection?.launch_date}
                  placeholder=""
                  disabled
                  key={state?.collection?.launch_date}
                />
              </div>
              <div className="col-lg-4 mb-0 form-group">
                <label className="form-label">Collection Licensor</label>
                <textarea
                  rows={1}
                  className="form-control d-inline no-resize horizontal-scroll"
                  value={state?.collection?.licensor?.name}
                  key={state?.collection?.licensor?.name}
                  disabled
                />
              </div>
              <div className="col-lg-4 mb-0 form-group">
                <label className="form-label">Collection License</label>
                <textarea
                  rows={1}
                  className="form-control d-inline no-resize horizontal-scroll"
                  value={state?.collection?.licensed_property?.name}
                  key={state?.collection?.licensed_property?.name}
                  disabled
                />
              </div>
            </div>

            <div className="col-md-1 mb-0 form-group">
              <label className="form-label">Collection Image</label>
              {!!state?.collection?.image &&
              state.collection?.image?.image_base64 ? (
                <div
                  className="img-container d-flex"
                  style={{ minHeight: "130px" }}
                >
                  <img
                    className="d-flex"
                    onClick={() => {
                      setImageToModal({
                        imageName: `Collection ${state.collection.name ?? ""}`,
                        image_file: null,
                        image_base64: `data:image/jpeg;base64,${state.collection.image?.image_base64}`,
                        isBlocked: true,
                      });
                      setShowPictureModal(true);
                    }}
                    alt="Image of the product"
                    src={`data:image/jpeg;base64,${state.collection.image?.image_base64}`}
                  />
                </div>
              ) : (
                <div className="img-placeholder d-flex justify-content-center align-items-center">
                  <span className="fw-bold text-center text-gray-800">
                    No Picture Available
                  </span>
                </div>
              )}
            </div>
          </div>

          <ExpandableSection
            className="mb-2 mt-4"
            sectionId="Create new Artwork"
            title={state?.id ? "Artwork" : "Create new Artwork"}
            expandedByDefault={true}
          >
            <div className="row mb-0">
              <div className="col-lg-2 form-group">
                <label className="form-label">Artwork Code</label>
                <input
                  value={state?.code}
                  placeholder=""
                  type="text"
                  className="form-control d-inline"
                  disabled={true}
                />
              </div>

              <div className="col-lg-5 form-group">
                <label className="form-label">
                  Artwork Name <span className="red-text fw-bold">*</span>
                </label>
                <input
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    dispatch(
                      PrintingTechniqueActions.setStateValue(
                        CreatePrintingTechniqueTypes.SET_NAME,
                        e.target.value,
                      ),
                    );
                  }}
                  value={state?.name}
                  placeholder=""
                  type="text"
                  className="form-control d-inline"
                  disabled={state?.isReadOnly}
                />
              </div>

              <div className="col-lg-5 form-group">
                <label className="form-label">
                  Mold(s) <span className="red-text fw-bold">*</span>
                </label>
                <AsyncSelect
                  cacheOptions
                  loadOptions={(input, callback) => {
                    searchDebounce(
                      loadMolds,
                      input,
                      getMolds(moldsFetched) ?? [],
                      callback,
                    );
                  }}
                  defaultOptions={getMolds(moldsFetched)}
                  onChange={(e: any) => {
                    dispatch(
                      PrintingTechniqueActions.setStateValue(
                        CreatePrintingTechniqueTypes.SET_MOLDS,
                        e.map((moldOption: any) => moldOption.value),
                      ),
                    );
                  }}
                  value={!!state.molds ? getMolds(state.molds) : null}
                  formatOptionLabel={OptionFormatter}
                  // isOptionDisabled={() =>
                  //   Predicates.isNotNullAndNotUndefined(state?.molds) &&
                  //   state?.molds.length >= 5
                  // }
                  isMulti
                  classNamePrefix="react-select"
                  placeholder="(min 3 characters, up to 5 molded components)"
                  isClearable
                  isLoading={isMoldsLoading}
                  components={{
                    IndicatorSeparator: () => null,
                  }}
                  isDisabled={state?.isReadOnly}
                />
              </div>
            </div>
            <div className="row mb-0 mt-1">
              <div className="col-lg-2 form-group">
                <label htmlFor="purchased_part" className="form-label">
                  Or purchased part / Sub-Assembly
                </label>
                <Select
                  classNamePrefix="react-select"
                  options={[
                    { label: "Yes", value: "Y" },
                    { label: "No", value: "N" },
                  ]}
                  defaultValue={{ label: "No", value: "N" }}
                  onChange={(e: any) => {
                    dispatch(
                      PrintingTechniqueActions.setStateValue(
                        CreatePrintingTechniqueTypes.SET_PURCHASED_PARTS,
                        e ? e.value : null,
                      ),
                    );
                  }}
                  isDisabled={state?.isReadOnly}
                />
              </div>
              <div className="col-lg-5">
                <label className="form-label">
                  Decoration Technique{" "}
                  <span className="red-text fw-bold">*</span>
                </label>
                <Select
                  options={getDecorTechs(
                    decorationTechs.slice(1, decorationTechs.length),
                  )}
                  onChange={(e: any) => {
                    dispatch(
                      PrintingTechniqueActions.setStateValue(
                        CreatePrintingTechniqueTypes.SET_DECORATION_TECHNIQUE,
                        e ? e.value : null,
                      ),
                    );
                  }}
                  value={
                    Predicates.isNotNullAndNotUndefined(
                      state?.decoration_technique,
                    )
                      ? formatDecorTechIntoOption(state?.decoration_technique)
                      : null
                  }
                  placeholder=""
                  formatOptionLabel={OptionFormatter}
                  classNamePrefix="react-select"
                  isLoading={isDecoTechsLoading}
                  isClearable
                  components={{
                    IndicatorSeparator: () => null,
                  }}
                  isDisabled={
                    state?.isReadOnly ||
                    ["APPROVED", "REJECTED", "EDITED"].includes(
                      `${state.status?.name}`,
                    )
                  }
                />
              </div>

              <div className="col-lg-5">
                <label className="form-label">
                  Artwork Color <span className="red-text fw-bold">*</span>
                </label>
                <AsyncSelect
                  cacheOptions
                  loadOptions={(input, callback) => {
                    searchDebounce(
                      loadColorOptions,
                      input,
                      getColors(colors),
                      callback,
                    );
                  }}
                  defaultOptions={getColors(colors)}
                  onChange={(e: any) => {
                    dispatch(
                      PrintingTechniqueActions.setStateValue(
                        CreatePrintingTechniqueTypes.SET_COLOR,
                        e ? e.value : null,
                      ),
                    );
                  }}
                  value={
                    Predicates.isNotNullAndNotUndefined(state?.color?.name)
                      ? formatColorIntoOption(state?.color)
                      : null
                  }
                  placeholder="(min 3 characters)"
                  formatOptionLabel={OptionFormatter}
                  classNamePrefix="react-select"
                  isClearable
                  isLoading={isColorsLoading}
                  components={{
                    IndicatorSeparator: () => null,
                  }}
                  isDisabled={state?.isReadOnly}
                />
              </div>

              <div className="row p-0 mt-1">
                <div className="col-md-auto form-group mb-0">
                  <label className="form-label">Technical file</label>
                  <button
                    onClick={handlePictureModal}
                    type="button"
                    className="icon-button upload-button form-control"
                  >
                    {!!state.image || !!state.picture ? (
                      <img
                        src={
                          process.env.PUBLIC_URL +
                          "/assets/img/uploadSuccess.png"
                        }
                        alt="upload success"
                        className=""
                        width={30}
                      ></img>
                    ) : (
                      <img
                        src={
                          process.env.PUBLIC_URL +
                          "/assets/img/uploadPending.png"
                        }
                        alt="upload pending"
                        className=""
                        width={26}
                      ></img>
                    )}
                  </button>
                </div>

                <div className="col-md-auto form-group flex-fill">
                  <label className="form-label">
                    Comments and Technical Requests
                  </label>
                  <textarea
                    onChange={(e) => {
                      dispatch(
                        PrintingTechniqueActions.setStateValue(
                          CreatePrintingTechniqueTypes.SET_TECHNICAL_FILE_COMMENT,
                          e.target.value,
                        ),
                      );
                    }}
                    value={state?.technical_file_comment}
                    placeholder="Free-text field, expandable, max 500 characters?"
                    maxLength={500}
                    rows={1}
                    className="form-control d-inline"
                    disabled={state?.isReadOnly}
                    // style={{
                    //   height:
                    //     state?.isReadOnly && !!tecnicalFileImg ? "80%" : "",
                    //   maxHeight: "128px",
                    // }}
                  />
                </div>
              </div>
            </div>
          </ExpandableSection>

          {state.history.length > 0 && (
            <ArtworkRequestHistoryTable historyRecords={state.history} />
          )}

          {!state?.isValid && !state?.isReadOnly && (
            <>
              <Banner
                classname={"mt-3"}
                message={getMissingConditionsMessage()}
                type="danger"
              />
            </>
          )}

          <ArtworkPrintingTechniqueActionButtonsGroup
            isReadOnlyRequest={state.isReadOnly}
            isEditing={false}
            isSaveOrSubmitInProgress={isSaveOrSubmitInProgress}
            username={username ?? ""}
            hasApproverPermission={hasApproverPermission}
            hasCreatorPermission={hasCreatorPermission}
            setIsSaveOrSubmitInProgress={setIsSaveOrSubmitInProgress}
          />
        </div>
      )}
      <ReactModal
        isOpen={showUploadModal}
        className="custom-modal barcode-img-modal"
        overlayClassName="custom-overlay"
      >
        <ArtworkUploadModal
          setArtworkImage={(image: any) => {
            dispatch(
              PrintingTechniqueActions.setStateValue(
                CreatePrintingTechniqueTypes.SET_IMAGE,
                image,
              ),
            );
          }}
          uploadedImage={state.image}
          handleCloseModal={() => setShowUploadModal(false)}
        />
      </ReactModal>

      <ReactModal
        isOpen={showPictureModal}
        className="custom-modal mold-comps"
        overlayClassName="custom-overlay"
      >
        <ArtworkPictureModal
          handleCloseModal={() => setShowPictureModal(false)}
          product={imageToModal}
          setImage={(image: any) => {
            dispatch(
              PrintingTechniqueActions.setStateValue(
                CreatePrintingTechniqueTypes.SET_IMAGE,
                image,
              ),
            );
            dispatch(
              PrintingTechniqueActions.setStateValue(
                CreatePrintingTechniqueTypes.SET_PICTURE,
                image,
              ),
            );
          }}
        />
      </ReactModal>
    </>
  );
};
